import Button from '@/shared/Button';
import Paragraph from '@/shared/Paragraph';
import Div from '@/shared/Div';
import Form from '@/shared/Form';
import OtpInput from '@/shared/OtpInput';
import React, { useState, useEffect, memo } from 'react';
import { useForm } from 'react-hook-form';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { otpSend } from '@/actions/ecom/login';
import { otpVerify } from '@/actions/ecom/otpVerify';
import { useRouter } from 'next/router';
import FixedWrapper from '@/shared/FixedWrapper';
import useScreen from '@/hooks/useScreen';
import classNames from 'classnames';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import dlv from 'dlv';
import { LOGIN_REDIRECT_TO_QUERY, WE_PHONE } from '@/constants/app';
import { AMC_DEVICE_PAGE_URL, ECOM_HOME_URL } from '@/configs/constants';
import { useAppConfigContext } from '@/context/AppContext';
import { getProfileV2 } from '@/actions/fetchProfile';
import { useProfileContext } from '@/context/profileContext';
import LocalStorage from '@/utils/localStorage';
import is from '@/utils/is';
import checkIfTSAAppIsInstalled from '@/utils/checkIfTSAAppIsInstalled';
import { deleteLogoutTokens } from '@/utils/deleteLogoutTokens';
import { logout } from '@/actions/ecom/logout';

interface LoginData {
  title?: string;
  content?: string;
  className?: string;
  btnClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  mobileBtnClassName?: string;
}
type OtpProps = {
  mobileNumber: string;
  boxNumber?: number;
  loginData?: LoginData;
  redirectTo?: string;
  userId: string;
  callback?: Function;
};
type ButtonProps = {
  isLoading?: boolean;
  otp?: string;
  isMobile?: boolean;
  boxNumber?: number;
  loginData?: LoginData;
};

const renderButton = ({ isLoading, otp, isMobile, boxNumber, loginData }: ButtonProps) => {
  return (
    <Button
      className={classNames('w-full rounded', {
        'mt-16': !isMobile,
        [loginData?.btnClassName || '']: !!loginData?.btnClassName,
      })}
      type="submit"
      disabled={Boolean(isLoading || !otp || otp.length != boxNumber)}
      isLoading={isLoading}
    >
      Verify
    </Button>
  );
};

// const renderErrorMessage = ({
//   isError,
//   data,
//   error,
//   isVerifyButtonClicked,
//   setVerifyButtonClicked,
//   isMobile,
// }: any) => {
//   if (isError) {
//     return (
//       <Toast
//         message={error?.message}
//         severity="error"
//         position={isMobile ? 'top' : 'bottom'}
//         // position="bottom"
//         show={isVerifyButtonClicked && isError}
//         onClose={() => setVerifyButtonClicked(false)}
//         durationInms={1000}
//       />
//     );
//   } else if (!data?.success) {
//     return (
//       <Toast
//         message={data?.message}
//         severity="error"
//         position={isMobile ? 'top' : 'bottom'}
//         //position="bottom"
//         show={isVerifyButtonClicked && !data?.success}
//         onClose={() => setVerifyButtonClicked(false)}
//         durationInms={6000}
//       />
//     );
//   } else {
//     return null;
//   }
// };

const Otp = ({
  mobileNumber,
  userId,
  boxNumber = 4,
  loginData,
  redirectTo,
  callback,
}: OtpProps) => {
  const [otp, setOtp] = useState('');
  const [isVerifyButtonClicked, setVerifyButtonClicked] = useState(false);
  const [disableResend, setResendDisable] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(30);
  const [emptyInputBox, setEmptyInputBox] = useState(false);
  const { isMobile } = useScreen();
  const { state: appConfig, dispatch: appConfigDispatch } = useAppConfigContext();
  const { dispatch: profileDispatch } = useProfileContext();
  const { fireGTMEvent } = useGTM();
  const { isFlutterClient } = appConfig;
  const [resendOtpError, setResendOtpError] = useState('');
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();
  const router = useRouter();
  const fetch = (...args: any) => otpVerify.apply(null, args);

  const { data, isLoading, promisifyTrigger, isError, error } = useTriggerRequest({ fetch });

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;

    if (disableResend && resendCountdown > 0) {
      countdownTimer = setTimeout(() => {
        setResendCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (resendCountdown === 0 && !isLoading) {
      setResendDisable(false);
    }

    return () => {
      clearTimeout(countdownTimer);
    };
  }, [disableResend, resendCountdown, isLoading]);

  useEffect(() => {
    fireGTMEvent(GTMEventNames.OTP_SCREEN_LANDED, {
      screen_name: SCREEN_NAMES.LOGIN_OTP,
    });
  }, []);

  const handleResendClick = async () => {
    try {
      setVerifyButtonClicked(false);
      setOtp('');
      setResendOtpError('');
      setEmptyInputBox(true);
      const resendData = { mobile_number: mobileNumber };
      await otpSend(resendData);
      setResendCountdown(30);
      setResendDisable(true);
      setEmptyInputBox(false);
      fireGTMEvent(GTMEventNames.OTP_RESEND, {
        screen_name: SCREEN_NAMES.LOGIN_OTP,
      });
    } catch (error: any) {
      setResendOtpError(error?.data?.error as string);
      setResendCountdown(30);
      setResendDisable(true);
      setEmptyInputBox(false);
      console.error('Error resending OTP:', error);
    }
  };

  const onSubmit = async () => {
    setResendDisable(true);
    setVerifyButtonClicked(true);
    if (is.valid.otp(otp, boxNumber)) {
      const responseData = await promisifyTrigger({
        mobile_number: mobileNumber,
        otp: otp,
        userId,
      });
      fireGTMEvent(GTMEventNames.OTP_VERIFIED, {
        screen_name: SCREEN_NAMES.LOGIN_OTP,
        submission_error: !responseData?.success,
        error_message: !responseData?.success ? responseData?.message : '',
      });
      const sessionHash = dlv(responseData, 'data.data.session_id', null);
      if (responseData?.success || sessionHash) {
        if (!isFlutterClient && (await checkIfTSAAppIsInstalled())) {
          fireGTMEvent(
            GTMEventNames.TSA_APP_INSTALLED,
            {
              webengageId: dlv(responseData, 'data.data.webEngageId', null),
            },
            {
              webengage: {
                fire: true,
                isDirect: true,
              },
            },
          );
          await logout(sessionHash);
          deleteLogoutTokens();
          window.location.href = ECOM_HOME_URL;
          return;
        }
        LocalStorage.set(WE_PHONE, mobileNumber);
        const redirectParam = router.query?.[LOGIN_REDIRECT_TO_QUERY];
        // To prevent page reload after login
        if (redirectTo === '#') {
          const profileData = await getProfileV2();
          profileDispatch.loadProfile(profileData);
          appConfigDispatch.toggleLoginModal();
          if (callback) callback();
        } else {
          window.location.href = redirectParam
            ? redirectParam.toString()
            : redirectTo ?? AMC_DEVICE_PAGE_URL;
        }
      }
    }
  };

  const setOTP = () => setOtp;

  return (
    <Div
      className={classNames(
        'mt-8 md:p-10 md:bg-white md:rounded-xl  md:border md:border-core-outline-subtle',
        loginData?.className,
      )}
    >
      <Div className="flex flex-col gap-2 mb-8">
        <Paragraph
          className={classNames('text-h-1 md:text-hd-1 text-black/70', loginData?.titleClassName)}
        >
          Enter OTP
        </Paragraph>
        <Paragraph className="text-black/50">OTP sent to {mobileNumber}.</Paragraph>
      </Div>
      <Form name="otpForm" onSubmit={handleSubmit(onSubmit)}>
        <OtpInput
          data-testid="otpInput"
          name="otpLogin"
          boxNumber={boxNumber}
          value={otp}
          onSet={setOTP()}
          errors={errors}
          emptyInputBox={emptyInputBox}
          data={data}
        />

        {isVerifyButtonClicked && isError && error?.error && (
          <Div className="text-body-base text-red-500 my-s2">{error?.error}</Div>
        )}
        {resendOtpError ? (
          <Div className="text-body-base text-red-500 my-s2">{resendOtpError}</Div>
        ) : null}

        <Button
          theme="link"
          size="sm"
          className="text-blue-400 py-3 !px-0"
          onClick={handleResendClick}
          disabled={disableResend}
          data-testid="resendOtp"
        >
          {resendCountdown ? `Resend OTP (${resendCountdown}s)` : 'Resend OTP'}
        </Button>

        {isMobile ? (
          <FixedWrapper className={classNames('md:hidden p-6 pt-4', loginData?.mobileBtnClassName)}>
            {renderButton({ isLoading, otp, isMobile: true, boxNumber, loginData })}
          </FixedWrapper>
        ) : (
          renderButton({ isLoading, otp, isMobile: false, boxNumber, loginData })
        )}

        {/* {isVerifyButtonClicked && (isError || data?.success !== undefined) && (
          <>
            {renderErrorMessage({
              isError,
              data,
              error,
              isMobile,
              isVerifyButtonClicked,
              setVerifyButtonClicked,
            })}
          </>
        )} */}
      </Form>
    </Div>
  );
};

export default memo(Otp);
